import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { CalculatorSeoArticle } from '@/components/calculator/CalculatorSeoArticle';
import { ChartCard } from '@/components/calculator/ChartCard';
import { FieldExplainerGuide } from '@/components/calculator/FieldExplainerGuide';
import { NumberField } from '@/components/calculator/NumberField';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { ChartContainer, ChartLegendPills, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { getDebtPayoffFieldExplainers } from '@/content/field-explainers/debt-payoff';
import type { FieldExplainerContent } from '@/content/field-explainers/types';
import { calculateDebtPayoff, type DebtPayoffInput } from '@/engines/legacy-engines';
import {
  copyToClipboard,
  createContractShareUrl,
  exportPdfReport,
  exportRowsToCsv,
  formatCurrency,
  parseContractState
} from './shared';

type DebtState = {
  debts: Array<{ name: string; balance: number; rate: number; minPayment: number }>;
  extraPayment: number;
  selectedStrategy: 'minimum' | 'avalanche' | 'snowball';
};

const defaults: DebtState = {
  debts: [
    { name: 'Card A', balance: 6000, rate: 24, minPayment: 180 },
    { name: 'Card B', balance: 3500, rate: 18, minPayment: 110 },
    { name: 'Loan', balance: 12000, rate: 8, minPayment: 260 }
  ],
  extraPayment: 250,
  selectedStrategy: 'avalanche'
};

const chartConfig = {
  totalBalance: {
    label: 'Total balance',
    color: 'var(--chart-1)'
  }
} satisfies ChartConfig;

type Props = { route: RouteDefinition };

function toEngineInput(state: DebtState): DebtPayoffInput {
  return {
    debts: state.debts,
    extraPayment: state.extraPayment
  };
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) {
    return '$0';
  }

  if (Math.abs(value) < 1000) {
    return `$${Math.round(value)}`;
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1
  }).format(value);
}

export function DebtPayoffCalculatorPage({ route }: Props) {
  const { toast } = useToast();
  const [state, setState] = useState<DebtState>(() => parseContractState('debtPayoff', defaults));

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const result = useMemo(() => calculateDebtPayoff(toEngineInput(state)), [state]);

  const strategyRows = [
    { id: 'minimum', label: 'Minimum', data: result.minimum },
    { id: 'avalanche', label: 'Avalanche', data: result.avalanche },
    { id: 'snowball', label: 'Snowball', data: result.snowball }
  ] as const;

  const selectedData = strategyRows.find((row) => row.id === state.selectedStrategy)?.data ?? result.avalanche;
  const chartData = selectedData.monthlyData.map((point) => ({
    month: point.month,
    totalBalance: point.totalBalance
  }));

  // The engine caps its schedule at 600 months. When `paidOff` is false the
  // balances never actually reach zero (interest outpaces payments), so the
  // month count is a floor, not a real payoff date — present it as "50+ years".
  const selectedPaidOff = selectedData.paidOff !== false;
  // The engine caps its schedule at 600 months. A capped plan can still be
  // valid-but-slow (balance shrinking, would clear after 50 years) rather than
  // impossible (interest outpaces payments, balance flat or growing). Compare
  // the balance over the last stretch of the schedule to tell them apart, so we
  // don't tell someone whose debt is steadily shrinking that it "never pays off".
  const cappedButShrinking = (() => {
    if (selectedPaidOff) return false;
    const points = selectedData.monthlyData;
    const lookback = Math.min(12, points.length - 1);
    if (lookback < 1) return false;
    const latest = points[points.length - 1].totalBalance;
    const earlier = points[points.length - 1 - lookback].totalBalance;
    return latest < earlier - 0.01;
  })();
  // Growing = capped and NOT shrinking: payments never cover interest, so the
  // balance and the accrued-interest total both run away. Neither the month
  // count nor the interest figure is meaningful in that case (they are just
  // artifacts of the 600-month cap), so present them qualitatively.
  const debtGrowing = !selectedPaidOff && !cappedButShrinking;
  const monthsToFreedomLabel = selectedPaidOff ? `${selectedData.months} months` : debtGrowing ? 'Never' : '50+ years';
  const totalInterestLabel = debtGrowing ? 'Rises indefinitely' : formatCurrency(selectedData.totalInterest);
  // A capped never-payoff minimum baseline has runaway months/interest, so
  // "saved vs minimum" deltas are only meaningful when the baseline clears too.
  const canCompareVsMinimum = selectedPaidOff && result.minimum.paidOff !== false;
  const monthsSavedVsMinimum = canCompareVsMinimum ? Math.max(result.minimum.months - selectedData.months, 0) : 0;
  const interestSavedVsMinimum = canCompareVsMinimum
    ? Math.max(result.minimum.totalInterest - selectedData.totalInterest, 0)
    : 0;

  const explainers = useMemo(() => {
    const entries = getDebtPayoffFieldExplainers({
      input: {
        debts: state.debts,
        extraPayment: state.extraPayment
      },
      result: {
        selectedStrategy: state.selectedStrategy,
        selectedPaidOff,
        cappedButShrinking,
        debtGrowing,
        canCompareVsMinimum,
        monthsToFreedomLabel,
        monthsSavedVsMinimum,
        totalInterestLabel,
        interestSavedVsMinimum,
        totalStartingDebt: state.debts.reduce((sum, debt) => sum + debt.balance, 0),
        minimumMonths: result.minimum.months,
        minimumInterest: result.minimum.totalInterest
      }
    });
    const byId = new Map<string, FieldExplainerContent>(entries.map((entry) => [entry.id, entry]));
    return { entries, byId };
  }, [
    state.debts,
    state.extraPayment,
    state.selectedStrategy,
    selectedPaidOff,
    cappedButShrinking,
    debtGrowing,
    canCompareVsMinimum,
    monthsToFreedomLabel,
    monthsSavedVsMinimum,
    totalInterestLabel,
    interestSavedVsMinimum,
    result.minimum.months,
    result.minimum.totalInterest
  ]);

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <h1 className="font-display text-4xl leading-tight md:text-5xl">Debt Payoff Calculator</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Compare minimum, avalanche, and snowball repayment strategies with full monthly balance timelines.
        </p>
      </section>

      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="before" />

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel title="Debt and payment assumptions" description="Adjust debt balances, APRs, minimums, and extra monthly payment.">
          <div className="grid gap-4 md:grid-cols-2">
            {state.debts.map((debt, index) => (
              <div key={debt.name} className="rounded-md border border-border bg-card/70 p-3">
                <p className="mb-3 text-sm font-semibold text-foreground">{debt.name}</p>
                <div className="grid gap-3">
                  <NumberField id={`debt-${index}-balance`} label="Balance" value={debt.balance} min={0} step={50} prefix="$" onChange={(value) => setState((prev) => ({ ...prev, debts: prev.debts.map((item, itemIndex) => (itemIndex === index ? { ...item, balance: value } : item)) }))} />
                  <NumberField id={`debt-${index}-rate`} label="APR" value={debt.rate} min={0} max={45} step={0.1} suffix="%" onChange={(value) => setState((prev) => ({ ...prev, debts: prev.debts.map((item, itemIndex) => (itemIndex === index ? { ...item, rate: value } : item)) }))} />
                  <NumberField id={`debt-${index}-min`} label="Minimum payment" value={debt.minPayment} min={0} step={10} prefix="$" onChange={(value) => setState((prev) => ({ ...prev, debts: prev.debts.map((item, itemIndex) => (itemIndex === index ? { ...item, minPayment: value } : item)) }))} />
                </div>
              </div>
            ))}
            <NumberField id="debtExtra" label="Extra monthly payment" value={state.extraPayment} min={0} step={10} prefix="$" explainer={explainers.byId.get('extraPayment')} onChange={(value) => setState((prev) => ({ ...prev, extraPayment: value }))} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          {!selectedPaidOff && cappedButShrinking ? (
            <div
              role="status"
              className="rounded-md border border-amber-500/50 bg-amber-500/10 p-4 text-amber-800 dark:text-amber-200"
            >
              <p className="flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                This plan takes over 50 years
              </p>
              <p className="mt-1 text-sm leading-6 text-amber-800/90 dark:text-amber-200/90">
                The balance is shrinking, but slowly — at these payments it would take more than 50 years to clear,
                beyond the range this tool projects. A higher minimum or a larger extra payment would pay it off far
                sooner.
              </p>
            </div>
          ) : null}
          {!selectedPaidOff && !cappedButShrinking ? (
            <div
              role="alert"
              className="rounded-md border border-rose-500/50 bg-rose-500/10 p-4 text-rose-700 dark:text-rose-200"
            >
              <p className="flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                This plan never pays off
              </p>
              <p className="mt-1 text-sm leading-6 text-rose-700/90 dark:text-rose-200/90">
                At these payments, the balances never reach zero — interest outpaces payments, so the debt keeps
                growing. Increase the monthly payments (higher minimums or a larger extra payment) to make progress.
              </p>
            </div>
          ) : null}
          <ResultSummaryCard label="Selected strategy" value={state.selectedStrategy.toUpperCase()} note="strategy shown in chart and table" trend="flat" explainer={explainers.byId.get('selectedStrategy')} />
          <ResultSummaryCard label="Time to debt-free" value={monthsToFreedomLabel} note={!selectedPaidOff ? (cappedButShrinking ? 'over 50 years at these payments' : 'interest outpaces payments') : canCompareVsMinimum ? `${monthsSavedVsMinimum} months faster vs minimum` : 'minimum-only payments never clear these debts'} trend={canCompareVsMinimum && monthsSavedVsMinimum > 0 ? 'up' : 'flat'} explainer={explainers.byId.get('timeToDebtFree')} />
          <ResultSummaryCard label="Total interest" value={totalInterestLabel} note={!selectedPaidOff ? (cappedButShrinking ? 'slowly shrinking balance' : 'payments do not cover interest') : canCompareVsMinimum ? `${formatCurrency(interestSavedVsMinimum)} saved vs minimum` : 'minimum-only payments never clear these debts'} trend={!selectedPaidOff ? 'flat' : canCompareVsMinimum && interestSavedVsMinimum > 0 ? 'up' : 'flat'} explainer={explainers.byId.get('totalInterest')} />
          <ResultSummaryCard label="Total starting debt" value={formatCurrency(state.debts.reduce((sum, debt) => sum + debt.balance, 0))} note="sum of all balances" trend="flat" explainer={explainers.byId.get('totalStartingDebt')} />

          <div className="rounded-md border border-border bg-card/70 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Display strategy</p>
            <div className="flex flex-wrap gap-2">
              {strategyRows.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${state.selectedStrategy === row.id ? 'border-primary bg-primary/15 text-foreground' : 'border-border bg-background/70 text-muted-foreground hover:text-foreground'}`}
                  onClick={() => setState((prev) => ({ ...prev, selectedStrategy: row.id }))}
                >
                  {row.label}
                </button>
              ))}
            </div>
          </div>

          <ShareExportRow
            onCopyShare={async () => {
              const shareUrl = createContractShareUrl(route.canonicalPath, 'debtPayoff', {
                debts: state.debts,
                extraPayment: state.extraPayment,
                selectedStrategy: state.selectedStrategy
              });
              const ok = await copyToClipboard(shareUrl);
              toast({ title: ok ? 'Share link copied' : 'Share link ready', description: ok ? shareUrl : `Copy manually: ${shareUrl}`, variant: ok ? 'success' : 'warning' });
            }}
            onExportCsv={() => {
              const headers = ['Month', 'Total Balance'];
              const rows = selectedData.monthlyData.map((row) => [row.month, Math.round(row.totalBalance)]);
              exportRowsToCsv(headers, rows, `debt-payoff-${state.selectedStrategy}.csv`);
              toast({ title: 'CSV exported', description: `debt-payoff-${state.selectedStrategy}.csv downloaded.`, variant: 'success' });
            }}
            onExportPdf={async () => {
              const ok = await exportPdfReport({
                title: `Debt Payoff - ${state.selectedStrategy.toUpperCase()} Strategy`,
                subtitle: selectedPaidOff
                  ? `${selectedData.months} months to debt-free`
                  : cappedButShrinking
                    ? 'Over 50 years to pay off at these payments'
                    : 'Never pays off at these payments',
                summaryLeft: [
                  `Extra monthly payment: ${formatCurrency(state.extraPayment)}`,
                  `Total starting debt: ${formatCurrency(state.debts.reduce((sum, debt) => sum + debt.balance, 0))}`,
                  selectedPaidOff
                    ? `Months to payoff: ${selectedData.months}`
                    : cappedButShrinking
                      ? 'Time to payoff: 50+ years (slow but shrinking)'
                      : 'Time to payoff: 50+ years (never pays off)'
                ],
                summaryRight: [
                  `Total interest: ${totalInterestLabel}`,
                  ...(canCompareVsMinimum
                    ? [
                        `Months saved vs minimum: ${monthsSavedVsMinimum}`,
                        `Interest saved vs minimum: ${formatCurrency(interestSavedVsMinimum)}`
                      ]
                    : ['Minimum-only payments never clear these debts'])
                ],
                headers: ['Month', 'Total Balance'],
                rows: selectedData.monthlyData.map((row) => [row.month, formatCurrency(row.totalBalance)]),
                filename: `debt-payoff-${state.selectedStrategy}.pdf`
              });

              toast({
                title: ok ? 'PDF exported' : 'PDF export unavailable',
                description: ok ? `debt-payoff-${state.selectedStrategy}.pdf downloaded.` : 'PDF dependencies are not available right now.',
                variant: ok ? 'success' : 'warning'
              });
            }}
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <ChartCard
          title="Balance payoff curve"
          description="Monthly total debt balance for the selected strategy."
          legend={<ChartLegendPills config={chartConfig} keys={['totalBalance']} />}
        >
          <ChartContainer config={chartConfig} family="single">
            <LineChart
              accessibilityLayer
              data={chartData}
              margin={{
                top: 18,
                left: 12,
                right: 12
              }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} width={70} tickFormatter={(value) => formatCompactCurrency(Number(value))} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => formatCurrency(Number(value ?? 0))} />} />
              <Line dataKey="totalBalance" type="monotone" stroke="var(--color-totalBalance)" strokeWidth={3.2} dot={false} />
            </LineChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Debt payoff milestones" description="Month each debt is fully paid under the selected strategy.">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Debt</TableHead>
                <TableHead numeric>Payoff month</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedData.debtPayoffs.map((row) => {
                const payoffMonth = row.paidOffMonth ?? row.month;
                return (
                  <TableRow key={row.name}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell numeric>{payoffMonth == null ? 'Not paid off' : payoffMonth}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <ChartCard title="Monthly timeline" description="Complete month-by-month balance timeline for the selected strategy.">
          <Table containerClassName="max-h-[28rem]" stickyHeader compact>
            <TableHeader>
              <TableRow>
                <TableHead numeric>Month</TableHead>
                <TableHead numeric>Total balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedData.monthlyData.map((row) => (
                <TableRow key={row.month}>
                  <TableCell numeric>{row.month}</TableCell>
                  <TableCell numeric>{formatCurrency(row.totalBalance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ChartCard>
      </section>

      <section>
        <FieldExplainerGuide
          title="How each number is calculated"
          description="The extra-payment input and every result card, explained with your numbers filled in. Per-debt rows (balance, APR, minimum) feed the strategy directly and are covered inside the strategy explanation rather than field by field."
          entries={explainers.entries}
        />
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
      <CalculatorSeoArticle canonicalPath={route.canonicalPath} placement="after" />
    </AppLayout>
  );
}
